import { pdfActions, pdfSelectors, readerSelectors } from '@readcloud/state';

import { useAppDispatch } from '@readcloud/state';
import { OrderedSet } from 'immutable';
import { PDFPageProxy } from 'pdfjs-dist';
import React, { CSSProperties, RefObject, useContext } from 'react';
import { useSelector } from 'react-redux';
import { DocumentWithExtrasContext } from '../../context/DocumentWithExtras';

import { LoadingIcon } from '@readcloud/component-library';
import { usePDFGridNavigation } from '../../hooks/usePDFGridNavigation';
import { useZoom } from '../../hooks/useZoom';
import PDFPage from './PDFPage';

type Props = {
  containerRef: RefObject<HTMLDivElement>;
  outerHeight: number;
  outerWidth: number;
};

type CellProps = {
  scale: number;
  page: PDFPageProxy;
  containerRef: RefObject<HTMLDivElement>;
  innerContainerRef: RefObject<HTMLDivElement>;
  width: number;
  height: number;
  style: CSSProperties;
};

const PDFGridCell = ({
  scale,
  style,
  page,
  height,
  width,
  containerRef,
  innerContainerRef,
}: CellProps) => {
  const gridCellRef = React.useRef();
  return (
    <div
      style={style}
      data-pagenumber={page?.pageNumber}
      key={page?.pageNumber}
      ref={gridCellRef}
    >
      <PDFPage
        page={page}
        scale={scale}
        height={height}
        width={width}
        pdfViewerContainer={containerRef.current}
        pdfGridContainer={innerContainerRef.current}
        pdfGridCell={gridCellRef.current}
      />
    </div>
  );
};

export const PDFGrid = (props: Props) => {
  // #############################################################
  // The component is designed to only render a set number of cells at once, based on a current page number.
  // it will render a range of pages around the current page number, based on the pdf buffer settings.

  // The main process of how everything happens is as follows:
  // Using the pageNumber, get all pages within X range. e.g. If a user is on page 5, and they've set a buffer of 3, it will get pages [2,3,4,5,6,7,8].
  // These pages are stored in the pages state object.
  // When a page is retrieved from the pdfDocument.getPage() function, a callback (onGetPage()) is triggered.
  // This callback takes the new page and stores the height + width in the measuredCells state object, forever.
  // When the measuredCells state object changes, it triggers a useEffect that keeps track of the mostCommonWidth, and the mostCommonHeight of all previously measured pages.
  // We then use this to estimate the positions of pages where we haven't measured all of the previous pages that came before it. e.g. if you start on page 100, we use the mostCommonWidth and mostCommonHeight to
  // estimate where it should be placed in the container, assuming that all the pages that came before it have the same measurements as the most common pages.
  // We also record the cellBaseScale - the amount the most common width/height would need to be scaled to fit the screen.
  // and then we use this to figure out the countainerSize - the total size oft he container.

  // Anything related to deailing with the scroll position is handled by the usePDFGridNavigation hook.

  // The page number only dictates where the container scroll position should be specifically when the dimensions of the grid need to be entirely recalculated (e.g. when flow, gutterSize or conintuous mode change).
  // otherwise, the scroll position always dictates the pageNumber. This is to avoid circular logic. When zooming, the scroll position is multipled by the same scale as the zoom, so we don't need to use pageNumber.

  const {
    pdfDocument,
    location,
    setLocation,
    scale,
    progress,
    setTemporaryScale,
    temporaryScale,
    setElementOrigin,
    elementOrigin,
    setScreenOrigin,
    screenOrigin,
    extras,
  } = useContext(DocumentWithExtrasContext);

  const pdfGridContainerRef = React.useRef();
  const outerContainerRef = React.useRef();

  const pdfData = useSelector(pdfSelectors.getPDFData);
  const documentControl = useSelector(readerSelectors.getDocumentControl);

  const dispatch = useAppDispatch();

  const [pages, setPages] = React.useState<PDFPageProxy[]>([]);

  const [currentlyRenderingPages, setCurrentlyRenderingPages] = React.useState<
    number[]
  >([]);

  const [measuredCells, setMeasuredCells] = React.useState<{
    [pageNumber: string]: {
      size: { width: number; height: number };
    };
  }>({});

  //########################################################
  // Function for measuring the default page sizes of the pdf when pdfDocument.getPage(pagenumber) is called.
  //########################################################
  const onGetPage = (page: PDFPageProxy) => {
    let pageViewport = page.getViewport({ scale: 1 });

    //https://github.com/mozilla/pdf.js/issues/5628#issuecomment-343299126
    //In the html/css world 1in = 96pixels, in PDF the default is 1in = 72pixels
    const CSS_UNITS = 96 / 72;

    // Set the data for the current page in measuredCells
    setMeasuredCells((s) => ({
      ...s,
      [page.pageNumber]: {
        size: {
          width: pageViewport.width * CSS_UNITS,
          height: pageViewport.height * CSS_UNITS,
        },
      },
    }));
  };
  //#####################################################

  //#####################################################
  //Find the page numbers we need using the pdf bufferSize setting.
  //#####################################################
  React.useEffect(() => {
    const tempCurrentlyRenderingPages = [];

    let dynamicBufferSize = pdfData.settings.bufferSize;

    if (pdfDocument) {
      // work out what pages we currently want to render by using the buffer.

      //push the pagenumbers to an array. The order of this array dictates the order pages will be rendered.
      // for now it's set up with [pageNumber, pageNumber + 1, pageNumber - 1, pageNumber + 2, pageNumber - 2, ...]
      let tempLocation = parseInt(location);

      if (
        !Number.isInteger(tempLocation) ||
        tempLocation > pdfDocument.numPages ||
        tempLocation < 0
      ) {
        //if for whatever reason location is set to something outside of the bounds of the book, just set it to 1.
        tempLocation = 1;
        setLocation(tempLocation.toString());
      }

      tempCurrentlyRenderingPages.push(tempLocation);

      for (let i = 1; i <= dynamicBufferSize; i++) {
        if (tempLocation + i <= pdfDocument.numPages) {
          tempCurrentlyRenderingPages.push(tempLocation + i);
        }
        if (tempLocation - i >= 1) {
          tempCurrentlyRenderingPages.push(tempLocation - i);
        }
      }
      setCurrentlyRenderingPages(tempCurrentlyRenderingPages);
    }
  }, [pdfDocument, location, pdfData.settings.bufferSize, scale]);
  //#################################################

  //#################################################
  // when currentlyRenderingPages changes (the useEffect above), we then use this to actually get the pages from pdfDocument and start the cleanup of the pages we dont need.
  // we will first fill the measuredCells array with estimated data, so that we can render blank pages as a sign of loading, then replace them in the onGetPage().
  //#################################################
  React.useEffect(() => {
    const tempPages: React.SetStateAction<PDFPageProxy[]> = [...pages];
    if (mostCommonWidth && mostCommonHeight) {
      currentlyRenderingPages.forEach((pageNumber) => {
        //set measuredCells with estimated data if it doesn't exist;
        if (!measuredCells[pageNumber]) {
          setMeasuredCells((s) => ({
            ...s,
            [pageNumber]: {
              size: {
                width: mostCommonWidth,
                height: mostCommonHeight,
              },
            },
          }));
        }
      });
    }

    currentlyRenderingPages.forEach((pageNumber) => {
      // first check to see if the page is already present in pages Array.
      const currentPage = pages.find((page) => page.pageNumber === pageNumber);
      if (!currentPage) {
        if (pageNumber > 0 && pageNumber <= (pdfDocument?.numPages || 0)) {
          pdfDocument
            .getPage(pageNumber)
            .then((page) => {
              //call this to update measuredCells state with the new page's height/width.
              onGetPage(page);
              tempPages.push(page);
            })
            .catch((e) => {
              console.log(e);
            });
        }
      }
    });

    const pagesToCleanup = pages.filter(
      (page) => !currentlyRenderingPages.includes(page.pageNumber)
    );
    pagesToCleanup.forEach((page) => {
      if (page.cleanup()) {
        const index = tempPages.indexOf(page);
        tempPages.splice(index, 1);
      }
    });

    setPages(tempPages);
  }, [currentlyRenderingPages, pdfDocument]);
  //###############################################

  //###############################################
  // update mostCommonWidth and mostCommonHeight each time measuredCells changes.
  //###############################################
  const mostCommonWidth = React.useMemo(() => {
    if (!Object.keys(measuredCells).length) {
      return 0;
    }

    const widthArray: { [index: number]: number } = {};

    //create a map of width's with a corresponding amount they show up.
    //{"700": 10, "600": 1, ...}
    Object.values(measuredCells).forEach((cell) => {
      widthArray[cell.size.width]
        ? widthArray[cell.size.width]++
        : (widthArray[cell.size.width] = 1);
    });

    // sort that array in descending order.
    const sortedWidths = Object.keys(widthArray).sort((a, b) =>
      widthArray[parseFloat(a)] > widthArray[parseFloat(b)] ? -1 : 1
    );

    //Take the first key, parse it back to an float.
    //since we are just assigning a primitive to state, it wont rerender the component if it's the same value.
    return (sortedWidths[0] && parseFloat(sortedWidths[0])) || 0;
  }, [measuredCells]);

  const mostCommonHeight = React.useMemo(() => {
    if (!Object.keys(measuredCells).length) {
      return 0;
    }

    const heightArray: { [index: number]: number } = {};

    //create a map of width's with a corresponding amount they show up.
    //{"700": 10, "600": 1, ...}
    Object.values(measuredCells).forEach((cell) => {
      heightArray[cell.size.height]
        ? heightArray[cell.size.height]++
        : (heightArray[cell.size.height] = 1);
    });

    // sort that array in descending order.
    const sortedHeights = Object.keys(heightArray).sort((a, b) =>
      heightArray[parseFloat(a)] > heightArray[parseFloat(b)] ? -1 : 1
    );

    //Take the first key, parse it back to an float.
    //since we are just assigning a primitive to state, it wont rerender the component if it's the same value.
    return (sortedHeights[0] && parseFloat(sortedHeights[0])) || 0;
  }, [measuredCells]);
  //##################################################
  const gutterSize = React.useMemo(
    () => pdfData.settings.gutterSize * (scale || 1),
    [pdfData.settings.gutterSize, scale]
  );
  // ##############################################
  // set the cell base scale
  // ##############################################
  const cellBaseScale = React.useMemo(() => {
    let tempBaseScale = 1;
    if (mostCommonHeight > 0 && mostCommonWidth > 0) {
      if (
        mostCommonHeight / props.outerHeight >
        mostCommonWidth / props.outerWidth
      ) {
        tempBaseScale = (props.outerHeight - gutterSize) / mostCommonHeight;
      } else {
        tempBaseScale = (props.outerWidth - gutterSize) / mostCommonWidth;
      }
    }
    return tempBaseScale;
  }, [
    mostCommonHeight,
    props.outerHeight,
    mostCommonWidth,
    props.outerWidth,
    pdfData.settings.gutterSize,
  ]);
  //###############################################

  //The scale everything needs to be multiplied by
  const viewScale = React.useMemo(
    () => cellBaseScale * (scale || 1),
    [cellBaseScale, scale]
  );

  const leftMargin = React.useMemo(() => {
    if (!Object.keys(measuredCells).length) return 0;
    // we want to base the left margin offset off of the first page's width if the flow is horizontal,
    // otherwise we want to base it off the most common width.
    const offset =
      pdfData.settings.flow === 'Horizontal'
        ? measuredCells[Object.keys(measuredCells)[0]].size.width
        : mostCommonWidth;
    return Math.max(0, (props.outerWidth - offset * viewScale) / 2);
  }, [viewScale, pdfData.settings.flow]);

  const rightMargin = React.useMemo(() => {
    if (!Object.keys(measuredCells).length) return 0;
    // we want to base the right margin offset off of the first page's width if the flow is horizontal,
    // otherwise we want to base it off the most common width.
    const offset =
      pdfData.settings.flow === 'Horizontal'
        ? measuredCells[
            Object.keys(measuredCells)[Object.keys(measuredCells).length - 1]
          ].size.width
        : mostCommonWidth;
    return Math.max(0, (props.outerWidth - offset * viewScale) / 2);
  }, [viewScale, pdfData.settings.flow, mostCommonWidth]);

  const topMargin = React.useMemo(() => {
    if (!Object.keys(measuredCells).length) return 0;
    // we want to base the top margin offset off of the first page's height if the flow is vertical,
    // otherwise we want to base it off the most common height.
    const offset =
      pdfData.settings.flow === 'Vertical'
        ? measuredCells[Object.keys(measuredCells)[0]].size.height
        : mostCommonHeight;
    return Math.max(0, (props.outerHeight - offset * viewScale) / 2);
  }, [viewScale, pdfData.settings.flow, mostCommonHeight]);

  const bottomMargin = React.useMemo(() => {
    if (!Object.keys(measuredCells).length) return 0;
    // we want to base the bottom margin offset off of the last page's height if the flow is vertical,
    // otherwise we want to base it off the most common height.
    const offset =
      pdfData.settings.flow === 'Vertical'
        ? measuredCells[
            Object.keys(measuredCells)[Object.keys(measuredCells).length - 1]
          ].size.height
        : mostCommonHeight;
    return Math.max(0, (props.outerHeight - offset * viewScale) / 2);
  }, [viewScale, pdfData.settings.flow, mostCommonHeight]);

  const { prevScrollPosition } = useZoom({
    scale: temporaryScale,
    setScale: setTemporaryScale,
    setElementOrigin: setElementOrigin,
    setScreenOrigin: setScreenOrigin,
    viewportEl: outerContainerRef,
    containerEl: pdfGridContainerRef,
    minScale: 1,
    maxScale: 4,
  });

  // ##############################################
  // update containerSize needed to fit all pages, using measuredSize and extraEstimatedSize
  // ##############################################
  const measuredSize = React.useMemo(() => {
    const size = { width: 0, height: 0 };
    if (pdfData.settings.flow === 'Horizontal') {
      Object.keys(measuredCells).forEach((cell) => {
        const tempWidth =
          measuredCells[cell].size.width * viewScale + gutterSize;

        if (pdfData.settings.continuous) {
          size.width += tempWidth;
        } else {
          size.width += Math.max(tempWidth, props.outerWidth);
        }
      });

      size.width -= gutterSize;
      size.height = mostCommonHeight * viewScale;
    } else {
      Object.keys(measuredCells).forEach((cell) => {
        const tempHeight =
          measuredCells[cell].size.height * viewScale + gutterSize; //allow for padding on each side if horizontal
        if (pdfData.settings.continuous) {
          size.height += tempHeight;
        } else {
          size.height += Math.max(tempHeight, props.outerHeight);
        }
      });
      size.height -= gutterSize;
      size.width = mostCommonWidth * viewScale;
    }
    return size;
  }, [
    measuredCells,
    mostCommonHeight,
    mostCommonWidth,
    pdfData.settings.flow,
    pdfData.settings.continuous,
    gutterSize,
    viewScale,
    props.outerWidth,
    props.outerHeight,
  ]);

  const extraEstimatedSize = React.useMemo(() => {
    let size = { width: 0, height: 0 };
    if (pdfDocument) {
      const numberOfUnmeasuredPages =
        (pdfDocument.numPages as number) - Object.keys(measuredCells).length;
      const tempPageWidth = mostCommonWidth * viewScale + gutterSize;

      const tempPageHeight = mostCommonHeight * viewScale + gutterSize;

      size = {
        width:
          pdfData.settings.flow === 'Horizontal'
            ? pdfData.settings.continuous
              ? numberOfUnmeasuredPages * tempPageWidth
              : numberOfUnmeasuredPages *
                Math.max(tempPageWidth, props.outerWidth)
            : 0,
        height:
          pdfData.settings.flow === 'Vertical'
            ? pdfData.settings.continuous
              ? numberOfUnmeasuredPages * tempPageHeight
              : numberOfUnmeasuredPages *
                Math.max(tempPageHeight, window.innerHeight)
            : 0,
      };
    }
    return size;
  }, [
    measuredCells,
    mostCommonHeight,
    mostCommonWidth,
    pdfData.settings.flow,
    pdfData.settings.continuous,
    gutterSize,
    pdfDocument?.numPages,
    viewScale,
    props.outerWidth,
    props.outerHeight,
  ]);

  const containerSize = React.useMemo(() => {
    let tempWidth =
      Math.round((measuredSize.width + extraEstimatedSize.width) * 100) / 100;
    let tempHeight =
      Math.round((measuredSize.height + extraEstimatedSize.height) * 100) / 100;

    return {
      width: tempWidth,
      height: tempHeight,
    };
  }, [measuredSize, extraEstimatedSize]);
  //###############################################

  // ##############################################
  // wok out cell offsets
  // ##############################################
  const cellOffsets = React.useMemo(() => {
    const tempCellOffsets = {};
    Object.keys(measuredCells).forEach((pageNumber) => {
      //work out the offset.
      const measuredCellsWeCareAbout = Object.keys(measuredCells).filter(
        (comparePageNumber) => {
          return parseInt(comparePageNumber) < parseInt(pageNumber);
        }
      );

      let tempOffset = 0;

      measuredCellsWeCareAbout.forEach((comparePageNumber) => {
        //TODO - we need to worry about RTL
        let tempWidth =
          measuredCells[comparePageNumber].size.width * viewScale + gutterSize; //allow for padding on each side

        let tempHeight =
          measuredCells[comparePageNumber].size.height * viewScale + gutterSize; //allow for padding on each side;

        if (!pdfData.settings.continuous) {
          tempWidth = Math.max(tempWidth, props.outerWidth);

          tempHeight = Math.max(tempHeight, props.outerHeight);
        }

        tempOffset +=
          pdfData.settings.flow === 'Horizontal' ? tempWidth : tempHeight;
      });

      const amountOfUnmeasuredCellsBeforeCurrentPage =
        parseInt(pageNumber) - 1 - measuredCellsWeCareAbout.length;

      const tempCellWidth = mostCommonWidth * viewScale + gutterSize;

      const tempCellHeight = mostCommonHeight * viewScale + gutterSize;

      const offsetCausedByUnmeasuredCells =
        pdfData.settings.flow === 'Horizontal'
          ? pdfData.settings.continuous
            ? amountOfUnmeasuredCellsBeforeCurrentPage * tempCellWidth
            : amountOfUnmeasuredCellsBeforeCurrentPage *
              Math.max(tempCellWidth, props.outerWidth)
          : pdfData.settings.continuous
          ? amountOfUnmeasuredCellsBeforeCurrentPage * tempCellHeight
          : amountOfUnmeasuredCellsBeforeCurrentPage *
            Math.max(tempCellHeight, props.outerHeight);

      tempOffset += offsetCausedByUnmeasuredCells;
      tempCellOffsets[pageNumber] = tempOffset;
    });
    return tempCellOffsets;
  }, [
    measuredCells,
    mostCommonHeight,
    mostCommonWidth,
    viewScale,
    pdfData.settings.flow,
    pdfData.settings.continuous,
    gutterSize,
    props.outerWidth,
    props.outerHeight,
  ]);

  // ##############################################
  // update redux with these values
  // ##############################################
  React.useEffect(() => {
    dispatch(
      pdfActions.updatePDFData({
        baseScale: cellBaseScale,
        scale: viewScale,
      })
    );
  }, [cellBaseScale, viewScale]);

  React.useEffect(() => {
    dispatch(
      pdfActions.updatePDFData({
        mostCommonHeight: mostCommonHeight,
        mostCommonWidth: mostCommonWidth,
      })
    );
  }, [mostCommonHeight, mostCommonWidth]);
  //###############################################

  usePDFGridNavigation({
    flow: pdfData.settings.flow,
    containerRef: outerContainerRef,
    cellOffsets: cellOffsets,
    measuredCells: measuredCells,
    leftMargin: leftMargin,
    topMargin: topMargin,
    outerWidth: props.outerWidth,
    outerHeight: props.outerHeight,
    viewScale: viewScale,
    continuous: pdfData.settings.continuous,
    gutterSize: gutterSize,
    unscaledGutterSize: pdfData.settings.gutterSize,
    mostCommonWidth: mostCommonWidth,
    mostCommonHeight: mostCommonHeight,
    containerSize: containerSize,
    screenOrigin: screenOrigin,
    prevScrollPosition: prevScrollPosition,
    documentControl: documentControl,
    location: location,
    setLocation: setLocation,
    pdfDocument: pdfDocument,
    linkService: extras.linkService,
  });

  return (
    <>
      <div
        ref={outerContainerRef}
        className={
          'rc-relative reader-scrollbar-fix rc-overflow-auto rc-flex-grow'
        }
        style={
          {
            // transformOrigin:
            //   screenOrigin && `${screenOrigin.x}px ${screenOrigin.y}px`,
            // transform: `scale(${temporaryScale / scale})`,
            // overflow: temporaryScale / scale !== 1 && 'hidden',
          }
        }
      >
        <div
          ref={pdfGridContainerRef}
          className="rc-relative"
          style={{
            width: containerSize.width + rightMargin,
            height: containerSize.height + bottomMargin,
            transformOrigin:
              elementOrigin && `${elementOrigin.x}px ${elementOrigin.y}px`,
            transform: `scale(${temporaryScale / scale})`,
            willChange: 'transform',
            marginLeft: leftMargin,
            marginTop: topMargin,
          }}
        >
          {!!cellBaseScale &&
            currentlyRenderingPages.map((pageNumber) => (
              <PDFGridCell
                key={pageNumber}
                style={{
                  position: 'absolute',
                  left:
                    pdfData.settings.flow === 'Horizontal' &&
                    cellOffsets[pageNumber],
                  top:
                    pdfData.settings.flow === 'Vertical' &&
                    cellOffsets[pageNumber],
                  width:
                    (measuredCells[pageNumber]?.size?.width ??
                      mostCommonWidth) * viewScale,
                  height:
                    (measuredCells[pageNumber]?.size?.height ??
                      mostCommonHeight) * viewScale,
                }}
                height={
                  (measuredCells[pageNumber]?.size?.height ??
                    mostCommonHeight) * viewScale
                }
                width={
                  (measuredCells[pageNumber]?.size?.width ?? mostCommonWidth) *
                  viewScale
                }
                page={pages.find((page) => page.pageNumber === pageNumber)}
                containerRef={props.containerRef}
                innerContainerRef={pdfGridContainerRef}
                scale={viewScale}
              />
            ))}
        </div>
      </div>

      <LoadingIcon
        loading={containerSize.width === 0 || containerSize.height === 0}
        progress={progress}
        position={'fixed'}
      />
    </>
  );
};
